import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { isEmail } from "class-validator";
import { getColors } from "../../themes/theme";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

export const SendDialog = ({ open, setOpen, id, type, protocolName }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      senderInfo: "",
    },
  });

  const { mainId, subId } = useParams();
  const { token, user } = useAuthContext();
  const [loading, setLoading] = useState(false); // Loading state
  const { enqueueSnackbar } = useSnackbar();
  const baseUrl = window.location.origin;
  const [selectedLanguage, setSelectedLanguage] = useState("English");

  const handlePreviewClick = async () => {
    setLoading(true);
    try {
      // Determine condition name based on protocol type
      const conditionName = type === "custom" && protocolName 
        ? `${protocolName}`
        : `${mainId} ${subId}`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/generate-pdf/exercise`,
        {
          userid: user?.userid,
          conditionName: conditionName,
          practiceid: user?.practiceId,
          protocolid: id,
          type,
          language: selectedLanguage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fileName = response.data.fileName.slice(0, -4);

      const isMobile =
        /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobile) {
        window.location.href = `${baseUrl}/shared_reports/${fileName}`;
      } else {
        window.open(`${baseUrl}/shared_reports/${fileName}`, "_blank");
      }
    } catch (err) {
      console.log("Error fetching PDF:", err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(data.senderInfo) && !isEmail(data.senderInfo)) {
      return;
    }

    try {
      // Determine condition name based on protocol type
      const conditionName = type === "custom" && protocolName 
        ? `${protocolName} - ${mainId} ${subId}`
        : `${mainId} ${subId}`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/generate-pdf/exercise`,
        {
          userid: user?.userid,
          conditionName: conditionName,
          practiceid: user?.practiceId,
          protocolid: id,
          type,
          language: selectedLanguage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fname = response.data.fileName.slice(0, -4);

      if (isEmail(data.senderInfo)) {
        setLoading(true);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/send-email/exercise`,
          {
            recipientAddress: data.senderInfo,
            content: `${user?.practiceName} recommends the following exercise plan.`,
            subject: `${user?.practiceName} recommends the following exercise plan.`,
            type: "exercise",
            fileName: fname,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else if (phoneRegex.test(data.senderInfo)) {
        setLoading(true);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/send-text/exercise`,
          {
            recipientAddress: data.senderInfo,
            content: `${baseUrl}/shared_reports`,
            subject: `${user?.practiceName} recommends the following exercise plan.`,
            type: "exercise",
            fileName: fname,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (err) {
      console.log("Error sending email:", err);
      enqueueSnackbar("Error sending email!", { variant: "error" });
    } finally {
      enqueueSnackbar("Successfully Sent!", { variant: "success" });
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Full-screen loading spinner */}
      <Backdrop sx={{ zIndex: 1400 }} open={loading}>
        <CircularProgress size={80} />
      </Backdrop>

      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={() => setOpen(false)}
      >
        <DialogContent
          sx={{
            px: { xs: 2, sm: "20px" },
            py: { xs: 2, sm: "20px" },
            background: getColors.listItemHoverBg,
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "18px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                    visibility: "hidden",
                  }}
                >
                  Enter
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "18px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                  }}
                >
                  Enter Patient's Email or Phone Number
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  value={selectedLanguage} // Bind the current language state
                  onChange={(e) => setSelectedLanguage(e.target.value)} // Handle language change
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    width: "60px",
                    "& .MuiFilledInput-input": {
                      fontFamily: "Inter",
                      fontSize: { xs: "15px", sm: "18px" },
                      fontWeight: 500,
                      padding: { xs: "5px 5px", sm: "5px 5px" },
                      background: getColors.drawerBgLight,
                      color: getColors.secondaryTextLight,
                      borderRadius: "5px",
                    },
                  }}
                >
                  <option value="English">EN</option>
                  <option value="Spanish">ES</option>
                  <option value="French">FR</option>
                </TextField>
              </Box>

              <TextField
                fullWidth
                variant="filled"
                placeholder="Enter email or phone number"
                sx={{
                  "& .MuiFilledInput-input": {
                    fontFamily: "Inter",
                    fontSize: { xs: "15px", sm: "18px" },
                    fontWeight: 500,
                    padding: { xs: "10px 20px", sm: "14px 24px" },
                    background: getColors.drawerBgLight,
                    color: getColors.secondaryTextLight,
                    borderRadius: "5px",
                  },
                }}
                error={!!errors.senderInfo}
                helperText={errors.senderInfo?.message}
                {...register("senderInfo", {
                  required: "This field is required",
                  validate: (value) =>
                    /^[0-9]{10}$/.test(value) || isEmail(value)
                      ? true
                      : "Please enter a valid 10-digit US phone number or a valid email",
                })}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: { xs: "16px", sm: "20px" },
              }}
            >
              <Button
                sx={{
                  width: "100%",
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: { xs: "14px", sm: "18px" },
                  fontWeight: 500,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: getColors.primaryTextDark,
                  color: getColors.primaryTextDark,
                  py: { xs: "8px", sm: "11px" },
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: getColors.buttonText,
                    color: getColors.primaryMain,
                  },
                }}
                onClick={handlePreviewClick}
                disabled={loading}
              >
                Preview
              </Button>

              <Button
                sx={{
                  width: "100%",
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: { xs: "14px", sm: "18px" },
                  fontWeight: 500,
                  background: getColors.buttonText,
                  color: getColors.thumbnailTextLight,
                  py: { xs: "8px", sm: "11px" },
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: getColors.primaryTextDark,
                    color: getColors.primaryMain,
                  },
                }}
                type="submit"
                disabled={loading}
              >
                Send
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
